import { generatePath, matchPath, useLocation, useParams } from 'react-router';
import { ProtectedSidebarLink, ProtectedSidebarLinkGroup } from '@britishcouncil/react-admin';

import { odpAssessmentAllowed, ROUTING_PATHS } from 'const';
import { useGetExaminerQuery, useHasRequiredTrainingForOdpQuery } from 'store';
import { usePermissions } from '@britishcouncil/react-common';
import { Permission } from 'api';

export const ExaminerSidebar = () => {
  const {
    examiner: {
      details,
      contactDetails,
      certification,
      odpAssessment,
      monitoring,
      trainingAndStandardisation,
      disciplinary,
      documents,
      centres,
      communication,
    },
    examiners,
  } = ROUTING_PATHS;
  const { pathname } = useLocation();
  const { examinerId } = useParams<{ examinerId: string }>();
  const { data: examiner } = useGetExaminerQuery(Number(examinerId), { skip: !examinerId });
  const { permissions } = usePermissions();
  const hasViewOdpPermission = permissions.includes(Permission.ViewOdp);

  const { data: hasRequiredTrainingForOdp } = useHasRequiredTrainingForOdpQuery(
    Number(examinerId),
    { skip: !examinerId || !hasViewOdpPermission }
  );

  const matchedExaminerPath = matchPath({ path: details.to, end: false }, pathname);
  const isOdpAssessmentAllowed =
    hasRequiredTrainingForOdp && odpAssessmentAllowed(examiner?.assignedRolesIds);
  if (
    !matchedExaminerPath ||
    //This additional condition is required to exclude examiners list routes which have the same base route.
    !!matchPath(examiners.add.path, pathname) ||
    !!matchPath(examiners.list.path, pathname) ||
    !!matchPath(examiners.certificationExpireSoonList.path, pathname) ||
    !!matchPath(examiners.certificationExpiredList.path, pathname) ||
    !!matchPath(examiners.monitoringExpireSoonList.path, pathname) ||
    !!matchPath(examiners.monitoringExpiredList.path, pathname)
  )
    return null;

  return (
    <>
      <ProtectedSidebarLinkGroup name="Selected Examiner" icon="user" expanded>
        <ProtectedSidebarLink
          {...details}
          to={generatePath(details.to, matchedExaminerPath.params)}
          name="Examiner details"
          icon="info circle"
          matchExactPathForActive={true}
        />
        <ProtectedSidebarLink
          {...contactDetails}
          to={generatePath(contactDetails.to, matchedExaminerPath.params)}
          name="Contact Info"
          icon="phone square"
          matchExactPathForActive={false}
        />
        <ProtectedSidebarLinkGroup
          name="Centres"
          icon="map marker"
          expanded
          requiresAny={centres.requiresAny}
        >
          <ProtectedSidebarLink
            {...centres.speaking}
            to={generatePath(centres.speaking.to, matchedExaminerPath.params)}
            name="Speaking"
            icon="comment outline"
            matchExactPathForActive={false}
          />
          <ProtectedSidebarLink
            {...centres.writing}
            to={generatePath(centres.writing.to, matchedExaminerPath.params)}
            name="Writing"
            icon="pencil"
            matchExactPathForActive={false}
          />
          <ProtectedSidebarLink
            {...centres.lifeSkills}
            to={generatePath(centres.lifeSkills.to, matchedExaminerPath.params)}
            name="Life Skills"
            icon="chart line"
            matchExactPathForActive={false}
          />
        </ProtectedSidebarLinkGroup>
        <ProtectedSidebarLink
          {...certification}
          to={generatePath(certification.to, matchedExaminerPath.params)}
          name="Certification"
          icon="graduation cap"
          matchExactPathForActive={false}
        />
        {isOdpAssessmentAllowed && (
          <ProtectedSidebarLink
            {...odpAssessment}
            to={generatePath(odpAssessment.to, matchedExaminerPath.params)}
            name="ODP Assessment"
            icon="book"
            matchExactPathForActive={false}
          />
        )}
        <ProtectedSidebarLink
          {...monitoring}
          to={generatePath(monitoring.to, matchedExaminerPath.params)}
          name="Monitoring"
          icon="eye"
          matchExactPathForActive={false}
        />
        <ProtectedSidebarLink
          {...trainingAndStandardisation}
          to={generatePath(trainingAndStandardisation.to, matchedExaminerPath.params)}
          name="Training & Standardisation"
          icon="sticky note"
          matchExactPathForActive={false}
        />
        <ProtectedSidebarLink
          {...disciplinary}
          to={generatePath(disciplinary.to, matchedExaminerPath.params)}
          name="Disciplinary"
          icon="balance scale"
          matchExactPathForActive={false}
        />
        <ProtectedSidebarLink
          {...documents}
          to={generatePath(documents.to, matchedExaminerPath.params)}
          name="Documents"
          icon="file text outline"
          matchExactPathForActive={false}
        />
        <ProtectedSidebarLink
          {...communication}
          to={generatePath(communication.to, matchedExaminerPath.params)}
          name="Communication"
          icon="envelope"
          matchExactPathForActive={false}
        />
      </ProtectedSidebarLinkGroup>
    </>
  );
};
